import type { Clock, IdFactory, StorageAdapter } from 'ProdantixSdk/core/types';

export interface SessionOptions {
  idleMs: number;
  maxMs: number;
  throttleMs: number;
}

export const DEFAULT_SESSION_OPTIONS: SessionOptions = { idleMs: 1_800_000, maxMs: 86_400_000, throttleMs: 10_000 };

interface SessionRecord {
  id: string;
  lastActivityAt: number;
  startedAt: number;
}

export interface CarriedSession {
  id: string;
  startedAt: number;
}

function sessionKey(namespace: string): string {
  return `pdx.${namespace}.session`;
}

export function adoptSession(storage: StorageAdapter, namespace: string, carried: CarriedSession, now: number): void {
  const record: SessionRecord = { id: carried.id, lastActivityAt: now, startedAt: carried.startedAt };
  storage.setItem(sessionKey(namespace), JSON.stringify(record));
}

export class SessionManager {
  constructor(
    private readonly storage: StorageAdapter,
    private readonly ids: IdFactory,
    private readonly clock: Clock,
    private readonly namespace: string,
    private readonly options: SessionOptions = DEFAULT_SESSION_OPTIONS,
  ) {}

  current(): string {
    return this.touch().id;
  }

  startedAt(): number {
    return this.touch().startedAt;
  }

  reset(): void {
    this.storage.removeItem(this.key());
  }

  private touch(): SessionRecord {
    const now = this.clock.now().getTime();
    const live = this.live(this.read(), now);
    if (live === undefined) {
      return this.write({ id: this.ids.uuid(), lastActivityAt: now, startedAt: now });
    }
    if (now - live.lastActivityAt <= this.options.throttleMs) {
      return live;
    }
    const latest = this.live(this.read(), now);
    const base = latest !== undefined && latest.id !== live.id ? latest : live;
    return this.write({ ...base, lastActivityAt: now });
  }

  private live(record: SessionRecord | undefined, now: number): SessionRecord | undefined {
    if (record === undefined) {
      return undefined;
    }
    if (now - record.lastActivityAt > this.options.idleMs || now - record.startedAt > this.options.maxMs) {
      return undefined;
    }
    return record;
  }

  private write(record: SessionRecord): SessionRecord {
    this.storage.setItem(this.key(), JSON.stringify(record));
    return record;
  }

  private read(): SessionRecord | undefined {
    const raw = this.storage.getItem(this.key());
    if (!raw) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(raw) as Partial<SessionRecord>;
      if (typeof parsed.id === 'string' && typeof parsed.startedAt === 'number' && typeof parsed.lastActivityAt === 'number') {
        return { id: parsed.id, lastActivityAt: parsed.lastActivityAt, startedAt: parsed.startedAt };
      }
      return undefined;
    } catch {
      return undefined;
    }
  }

  private key(): string {
    return sessionKey(this.namespace);
  }
}
